import { useEffect, useState } from "react";
import { api, ApiError } from "@/lib/api";
import { PageHeader } from "@/components/admin-ui";

type StatsResponse = {
  users: {
    total: number;
    active: number;
    suspended: number;
    pro: number;
    ordinary: number;
    verified: number;
    google_linked: number;
  };
  dau: number;
  mau: number;
  signups_by_day: Array<{ day: string; count: number }>;
  tokens_by_day: Array<{ day: string; input: number; output: number; turns: number }>;
};

export function StatsPage() {
  const [data, setData] = useState<StatsResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<StatsResponse>("/admin/accounts/stats");
        if (!cancelled) { setData(res); setErr(null); }
      } catch (e: any) {
        if (!cancelled) setErr(e instanceof ApiError ? e.message : String(e));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <>
      <PageHeader
        kicker="Accounts"
        title="Stats"
        sub="Live counters and 30-day trends. Drill into Users for per-account detail."
      />

      {err && <div style={{ ...errBanner, margin: "0 26px 14px" }}>{err}</div>}
      {!data && !err && <div style={{ padding: "0 26px", color: "var(--ink-3)", fontStyle: "italic" }}>Loading…</div>}

      {data && (
        <div style={{ padding: "0 26px 26px" }}>
          {/* KPI cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 18 }}>
            <Kpi label="Users (total)"   value={data.users.total} />
            <Kpi label="Active"          value={data.users.active}    tone="pos" />
            <Kpi label="Suspended"       value={data.users.suspended} tone={data.users.suspended > 0 ? "neg" : "muted"} />
            <Kpi label="Pro / agent"     value={data.users.pro} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
            <Kpi label="DAU (24h)"       value={data.dau} />
            <Kpi label="MAU (30d)"       value={data.mau} />
            <Kpi label="Verified"        value={data.users.verified}      sub={pct(data.users.verified, data.users.total)} />
            <Kpi label="Google-linked"   value={data.users.google_linked} sub={pct(data.users.google_linked, data.users.total)} />
          </div>

          {/* Two charts */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
            <Card title="Token burn (30d)">
              <TokenBurnChart series={data.tokens_by_day} />
              <Footnote series={data.tokens_by_day} />
            </Card>
            <Card title="Signups (30d)">
              <SignupsChart series={data.signups_by_day} />
              <FootnoteSignups series={data.signups_by_day} />
            </Card>
          </div>
        </div>
      )}
    </>
  );
}

function Kpi({ label, value, sub, tone }: { label: string; value: number; sub?: string; tone?: "pos" | "neg" | "muted" }) {
  const fg = tone === "pos" ? "var(--pos)" : tone === "neg" ? "var(--neg)" : "var(--ink)";
  return (
    <div style={{ border: "1px solid var(--rule)", borderRadius: 10, background: "var(--surface)", padding: "14px 16px" }}>
      <div style={kicker}>{label}</div>
      <div style={{ marginTop: 6, fontSize: 26, fontWeight: 500, letterSpacing: "-0.02em", color: fg, fontFamily: "var(--font-mono)" }}>
        {value.toLocaleString()}
      </div>
      {sub && <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ border: "1px solid var(--rule)", borderRadius: 10, background: "var(--surface)", padding: 16 }}>
      <div style={kicker}>{title}</div>
      <div style={{ marginTop: 10 }}>{children}</div>
    </div>
  );
}

function TokenBurnChart({ series }: { series: Array<{ day: string; input: number; output: number; turns: number }> }) {
  const max = Math.max(1, ...series.map((s) => s.input + s.output));
  return (
    <div style={{ height: 120, display: "flex", alignItems: "flex-end", gap: 2 }}>
      {series.map((s) => {
        const total = s.input + s.output;
        const inH = Math.round((s.input / max) * 120);
        const outH = Math.round((s.output / max) * 120);
        return (
          <div key={s.day} title={`${s.day}: ${total.toLocaleString()} tokens (${s.turns} turns)`} style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
            <div style={{ height: outH, background: "var(--ai-accent)", opacity: 0.85 }} />
            <div style={{ height: inH, background: "var(--accent)" }} />
          </div>
        );
      })}
    </div>
  );
}

function SignupsChart({ series }: { series: Array<{ day: string; count: number }> }) {
  const max = Math.max(1, ...series.map((s) => s.count));
  return (
    <div style={{ height: 120, display: "flex", alignItems: "flex-end", gap: 2 }}>
      {series.map((s) => {
        const h = Math.max(s.count > 0 ? 2 : 0, Math.round((s.count / max) * 120));
        return (
          <div key={s.day} title={`${s.day}: ${s.count} signup${s.count === 1 ? "" : "s"}`} style={{ flex: 1, height: h, background: s.count > 0 ? "var(--accent)" : "var(--surface-3)" }} />
        );
      })}
    </div>
  );
}

function Footnote({ series }: { series: Array<{ day: string; input: number; output: number; turns: number }> }) {
  const total = series.reduce((acc, s) => acc + s.input + s.output, 0);
  const turns = series.reduce((acc, s) => acc + s.turns, 0);
  return (
    <div style={{ marginTop: 8, fontSize: 11, color: "var(--ink-3)", display: "flex", justifyContent: "space-between" }}>
      <span>{total.toLocaleString()} tokens across {turns.toLocaleString()} turns</span>
      <span style={{ display: "flex", gap: 10 }}>
        <Legend color="var(--accent)" label="Input" />
        <Legend color="var(--ai-accent)" label="Output" />
      </span>
    </div>
  );
}
function FootnoteSignups({ series }: { series: Array<{ day: string; count: number }> }) {
  const total = series.reduce((acc, s) => acc + s.count, 0);
  return (
    <div style={{ marginTop: 8, fontSize: 11, color: "var(--ink-3)" }}>
      {total.toLocaleString()} new accounts in the last 30 days
    </div>
  );
}
function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <span style={{ width: 8, height: 8, background: color, borderRadius: 2 }} />
      {label}
    </span>
  );
}

function pct(n: number, d: number): string {
  if (d === 0) return "—";
  return `${Math.round((n / d) * 100)}%`;
}

const kicker: React.CSSProperties = { fontSize: 9.5, color: "var(--ink-4)", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase" };
const errBanner: React.CSSProperties = { background: "rgba(122,45,31,0.08)", color: "var(--neg)", padding: "10px 14px", borderRadius: 8, marginBottom: 14, fontSize: 13 };
